'use client'

import { useEffect } from 'react'

/* Delay between each element in a cascade batch. The fade duration
   itself lives in CSS (.reveal) and is identical for every element,
   so all transitions run at the same speed — only the start is staggered. */
const STAGGER_MS = 45

/* =========================================
   CASCADE REVEAL
   Fades ".reveal" elements in as they enter the viewport. Elements
   that appear together are staggered in document order to create a
   cascade, giving each page a consistent fade-in on load and scroll.
   ========================================= */
export function useCascadeReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const revealed = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) =>
            a.target.compareDocumentPosition(b.target) &
            Node.DOCUMENT_POSITION_FOLLOWING
              ? -1
              : 1
          )

        revealed.forEach((entry, index) => {
          const el = entry.target as HTMLElement
          el.style.transitionDelay = `${index * STAGGER_MS}ms`
          el.classList.add('visible')
          observer.unobserve(el)
        })
      },
      { threshold: 0.12 }
    )

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])
}
